import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { analytics } from "@/lib/analytics";
import { supabaseAuthClient } from "@/lib/auth-client";
import { safeRedirect } from "@/lib/utils";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/callback")({
  head: () => ({
    title: "Signing In - Setups by SZESE",
    meta: [
      ...seo({
        title: "Signing In - Setups by SZESE",
        description: "Completing sign in to Setups by SZESE.",
        noindex: true,
      }),
    ],
    links: [{ rel: "canonical", href: "https://setupsbyszese.com/auth/callback" }],
  }),
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  useEffect(() => {
    // supabaseAuthClient has detectSessionInUrl: true — it automatically
    // exchanges the PKCE code (or hash tokens) when the client initialises.
    // We listen for SIGNED_IN and then do a hard redirect so the server-side
    // cookie is present on the next page load.
    let redirected = false;

    const getNextAndRedirect = (session?: { user: { created_at: string } }) => {
      if (redirected) return;
      redirected = true;

      if (session) {
        const createdAt = new Date(session.user.created_at).getTime();
        const isNewUser = Date.now() - createdAt < 60_000;
        if (isNewUser) {
          analytics.signupCompleted({ method: "oauth" });
        } else {
          analytics.signinCompleted({ method: "oauth" });
        }
      }

      const stored = sessionStorage.getItem("auth_redirect_to") ?? "/app";
      sessionStorage.removeItem("auth_redirect_to");
      window.location.replace(safeRedirect(stored));
    };

    const {
      data: { subscription },
    } = supabaseAuthClient.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session) {
        getNextAndRedirect(session);
      }
    });

    // Handle the case where the session was already exchanged before the
    // listener was registered (e.g. fast machines).
    supabaseAuthClient.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        getNextAndRedirect(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <h1 className="sr-only">Signing in to Setups by SZESE</h1>
      <div className="absolute top-4 left-4">
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          &larr; Back to Setups by SZESE
        </Link>
      </div>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      <p className="text-muted-foreground text-sm">Signing you in...</p>
    </div>
  );
}
